import { isUndefined } from 'lodash-es';
import { Children, type ReactNode } from 'react';

import { Intent, type IntentProps } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import type { TrendProps } from '../../core/components/single-value-renderer/components/trend/types/trend-props.js';
import { iterateSparklineSlots } from '../../core/components/sparkline-display/utils/iterate-sparkline-slots.js';
import { ColorRule, isValidColorRule } from '../../core/slots/ColorRule.js';
import type { Timeseries } from '../../core/types/timeseries.js';
import { Sparkline } from '../slots/Sparkline.js';
import { Toolbar } from '../slots/Toolbar.js';
import { Trend } from '../slots/Trend.js';
import type { SingleValueToolbarSlotProps } from '../types/single-value-toolbar.js';
import type { SingleValueSparklineConfig } from '../types/single-value.js';
import type { ColorRuleProps } from './../../core/types/color-rule.js';

interface SingleValueSparklineSlotProps extends SingleValueSparklineConfig {
  data: Timeseries | Timeseries[];
}

export interface SingleValueSlots {
  sparkline?: SingleValueSparklineSlotProps;
  trend?: TrendProps;
  colorRules?: ColorRuleProps[];
  toolbar?: SingleValueToolbarSlotProps & { intents?: IntentProps[] };
}

export const iterateSlots = (
  children: ReactNode,
): { slots: SingleValueSlots; sparklineChildren: ReactNode } => {
  const slots: SingleValueSlots = {};
  let sparklineChildren: ReactNode;
  const colorRules: ColorRuleProps[] = [];
  let intents: IntentProps[] | undefined = undefined;

  Children.forEach(children, (child) => {
    if (isSlot(child, Sparkline)) {
      const { children: sparklineSlots, ...props } = child.props;
      const { thresholds, xAxis, yAxis } =
        iterateSparklineSlots(sparklineSlots);

      slots.sparkline = {
        ...props,
        ...(thresholds.length > 0 ? { thresholds } : {}),
        xAxis,
        yAxis,
      };
      sparklineChildren = sparklineSlots;
    } else if (isSlot(child, Trend)) {
      slots.trend = {
        ...child.props,
      };
    } else if (isSlot(child, ColorRule)) {
      const colorRule = child.props;
      if (isValidColorRule(colorRule)) {
        colorRules.push(colorRule);
      }
    } else if (isSlot(child, Intent)) {
      intents = intents ? [...intents, child.props] : [child.props];
    } else if (isSlot(child, Toolbar)) {
      slots.toolbar = child.props;
    }
  });

  if (colorRules.length > 0) {
    slots.colorRules = colorRules;
  }

  if (!isUndefined(intents)) {
    slots.toolbar = {
      ...slots.toolbar,
      intents,
      hidden: slots.toolbar?.hidden ?? false,
    };
  }

  return { slots, sparklineChildren };
};
